import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';

import {
  Briefcase,
  CurrencyDollar,
  FileText,
  Funnel,
  Lightning,
  MagnifyingGlass,
  Play,
  UsersThree
} from '@phosphor-icons/react';
/**
 * Workflows List - Phase 10: Workflow Chaining
 * Browse and run organizational workflows
 */
const WorkflowsList = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    if (activeOrganization) {
      fetchWorkflows();
    }
  }, [activeOrganization, categoryFilter]);

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchQuery) params.append('q', searchQuery);

      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/workflows?${params.toString()}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || []);
      }
    } catch (err) {
      console.error('Error fetching workflows:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchWorkflows();
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'ops': return Activity;
      case 'finance': return CurrencyDollar;
      case 'sales': return Briefcase;
      case 'hr': return UsersThree;
      default: return FileText;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'ops': return 'text-blue-400 bg-blue-500/20 border-blue-500/30';
      case 'finance': return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'sales': return 'text-purple-400 bg-purple-500/20 border-purple-500/30';
      case 'hr': return 'text-orange-400 bg-orange-500/20 border-orange-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="h-8 bg-gray-700 rounded w-64 mb-8 animate-pulse"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="glass rounded-xl p-6 h-64 animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Lightning size={32} className="text-[#6366F1]" />
            Workflows
          </h1>
          <p className="text-gray-400">Automate multi-step processes with pre-built workflows</p>
        </div>

        {/* MagnifyingGlass and Filters */}
        <div className="glass rounded-xl p-4 border border-[rgba(255,255,255,0.1)] mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* MagnifyingGlass */}
            <div className="flex-1 relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search workflows..."
                className="w-full pl-10 pr-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
              />
            </div>

            {/* Category Funnel */}
            <div className="relative">
              <Funnel size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-10 pr-8 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] appearance-none cursor-pointer"
              >
                <option value="">All Categories</option>
                <option value="ops">Operations</option>
                <option value="finance">Finance</option>
                <option value="sales">Sales</option>
                <option value="hr">Human Resources</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
        </div>

        {/* Workflows Grid */}
        {workflows.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center border border-[rgba(255,255,255,0.1)]">
            <Lightning size={64} className="text-gray-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">No Workflows Found</h2>
            <p className="text-gray-400 mb-6">
              {searchQuery || categoryFilter
                ? 'Try adjusting your search or filters'
                : 'No workflows have been created yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflows.map((workflow) => {
              const CategoryIcon = getCategoryIcon(workflow.category);
              const categoryColor = getCategoryColor(workflow.category);

              return (
                <div
                  key={workflow.workflow_id}
                  className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.1)] hover:border-[#6366F1] transition-all cursor-pointer group"
                  onClick={() => navigate(`/partner-pro/workspace/workflows/${workflow.workflow_id}`)}
                >
                  {/* Category Badge */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border mb-4 ${categoryColor}`}>
                    <CategoryIcon size={14} />
                    {workflow.category.toUpperCase()}
                  </div>

                  {/* Workflow Info */}
                  <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#6366F1] transition-colors">
                    {workflow.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                    {workflow.description}
                  </p>

                  {/* Steps Count */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {workflow.steps?.length || 0} steps
                    </span>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-opacity">
                      <Play size={16} />
                      Run
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowsList;
